/**
 * Normalize a dash array string for SVG stroke-dasharray attribute.
 * Converts various delimiters (commas, spaces) into a single space-separated string.
 * Returns undefined if the input is undefined or an empty string.
 *
 * @param dash - The dash array string to normalize.
 * @returns The normalized dash array string or undefined.
 */
export const normalizeDashArray = (dash?: string): string | undefined => {
  if (!dash) {
    return undefined;
  }
  // turn "5 5" or "2 2" into "5,5" or "2,2"
  return dash.trim().split(/\s+/).join(' ');
};

/**
 * Extract metadata from a dash array string, including the period length and type.
 * The type is determined based on the first number in the dash array:
 * - 'dots' if the first number is less than or equal to 2
 * - 'dashes' otherwise
 * If the input is undefined, returns undefined period and 'solid' type.
 *
 * @param dash - The dash array string to extract metadata from.
 * @returns An object containing the period length and type of dash.
 */
export const getDashMeta = (dash?: string) => {
  if (!dash) {
    return { period: undefined, type: 'solid' as const };
  }

  const nums = dash
    .trim()
    .split(/\s+/)
    .map(Number)
    .filter(n => n > 0);
  const period = nums.reduce((a, b) => a + b, 0);

  // crude heuristic: dots usually have very short "on" lengths
  const type = nums[0] <= 2 ? 'dots' : 'dashes';

  return { period, type };
};
